import React, { useMemo, useState } from "react";
import { AlertTriangle, Crown } from "lucide-react";
import LoanCreditGauge from "./LoanCreditGauge";
import {
  computeShareAllocation,
  formatPartnerCash,
  formatPendingCountdown,
} from "../../utils/partnersDeskHelpers";
import "../../operator-partners.css";

const DONUT = 120;
const STROKE = 9;
const R = (DONUT - STROKE) / 2;
const CIRC = 2 * Math.PI * R;

const compact = (value) => {
  const n = Number(value) || 0;
  const abs = Math.abs(n);
  const sign = n < 0 ? "−" : "";
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 10_000) return `${sign}${(abs / 1000).toFixed(0)}K`;
  if (abs >= 1000) return `${sign}${(abs / 1000).toFixed(1)}K`;
  return `${sign}${abs.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

const CapTableHero = ({ members, inviteQuote, pendingRequest, pendingInvites }) => {
  const allocation = useMemo(() => computeShareAllocation(members), [members]);
  const houseAvailable = Number(inviteQuote?.houseAvailable) || allocation.total;
  const buyIn = Number(inviteQuote?.amountRequired) || 0;
  const perPartner = members.length ? allocation.total / members.length : buyIn;

  const subParts = [];
  if (members.length) {
    subParts.push(
      `${members.length} partner${members.length === 1 ? "" : "s"} · ${formatPartnerCash(perPartner)} each`
    );
  } else {
    subParts.push("No partners on roster");
  }
  if (buyIn > 0) subParts.push(`${formatPartnerCash(buyIn)} buy-in`);

  return (
    <section className="oc-ptn-card oc-ptn-hero">
      <div className="oc-ptn-hero-main">
        <span className="oc-ptn-kicker">Cap table</span>
        <strong className="oc-ptn-hero-value tabular-nums">
          {formatPartnerCash(allocation.total || houseAvailable)}
        </strong>
        <span className="oc-ptn-hero-sub">{subParts.join(" · ")}</span>
      </div>

      {allocation.items.length > 0 ? (
        <>
          <div className="oc-ptn-comp-bar" aria-hidden>
            {allocation.items.map((seg) => (
              <span
                key={seg.key}
                className="oc-ptn-comp-seg"
                style={{
                  width: `${Math.max(seg.fraction * 100, 2)}%`,
                  background: seg.color,
                }}
              />
            ))}
          </div>
          <div className="oc-ptn-comp-legend">
            {allocation.items.map((seg) => (
              <span key={seg.key} className="oc-ptn-comp-key">
                <span className="oc-ptn-comp-dot" style={{ background: seg.color }} aria-hidden />
                <span className="oc-ptn-comp-name">
                  {seg.isOwner ? <Crown size={8} aria-hidden /> : null}
                  {seg.label}
                </span>
                <strong className="tabular-nums">{formatPartnerCash(seg.share)}</strong>
              </span>
            ))}
          </div>
        </>
      ) : null}

      {pendingRequest ? (
        <div className="oc-ptn-pending-strip">
          <AlertTriangle size={11} aria-hidden />
          <span>
            Invite waiting · {pendingInvites} pending · expires{" "}
            {formatPendingCountdown(pendingRequest.expiresAt)}
          </span>
        </div>
      ) : null}
    </section>
  );
};

const ShareDonut = ({ items, total }) => {
  const [activeKey, setActiveKey] = useState(null);

  if (!items.length) {
    return (
      <section className="oc-ptn-card oc-ptn-donut-card">
        <span className="oc-ptn-kicker">Ownership mix</span>
        <p className="oc-ptn-empty">Add partners to see split.</p>
      </section>
    );
  }

  const active = items.find((item) => item.key === activeKey) || null;
  let offset = 0;
  const segments = items.map((item) => {
    const seg = { ...item, dash: item.fraction * CIRC, offset };
    offset += item.fraction * CIRC;
    return seg;
  });

  return (
    <section className="oc-ptn-card oc-ptn-donut-card">
      <span className="oc-ptn-kicker">Ownership mix</span>
      <div className="oc-ptn-donut-wrap">
        <svg viewBox={`0 0 ${DONUT} ${DONUT}`} className="oc-ptn-donut" role="img" aria-label="Partner share mix">
          <circle
            cx={DONUT / 2}
            cy={DONUT / 2}
            r={R}
            fill="none"
            stroke="rgba(255,255,255,0.05)"
            strokeWidth={STROKE}
          />
          {segments.map((seg) => (
            <circle
              key={seg.key}
              cx={DONUT / 2}
              cy={DONUT / 2}
              r={R}
              fill="none"
              stroke={seg.color}
              strokeWidth={activeKey === seg.key ? STROKE + 2 : STROKE}
              strokeDasharray={`${Math.max(seg.dash - 1, 0.5)} ${CIRC}`}
              strokeDashoffset={-seg.offset}
              transform={`rotate(-90 ${DONUT / 2} ${DONUT / 2})`}
              className="oc-ptn-donut-seg"
              opacity={activeKey && activeKey !== seg.key ? 0.35 : 1}
              onPointerEnter={() => setActiveKey(seg.key)}
              onPointerLeave={() => setActiveKey(null)}
            />
          ))}
        </svg>
        <div className="oc-ptn-donut-center">
          {active ? (
            <>
              <span>{active.label}</span>
              <strong className="tabular-nums">{Math.round(active.fraction * 100)}%</strong>
            </>
          ) : (
            <>
              <span>Pool</span>
              <strong className="tabular-nums">{compact(total)}</strong>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

const ReadinessCard = ({ eligibilityChecked, allowed, checks, checksPassed }) => {
  const total = checks.length;
  const fraction = total > 0 ? checksPassed / total : 0;
  const failed = checks.filter((c) => !c.ok);

  return (
    <section className="oc-ptn-card oc-ptn-ready-card">
      <span className="oc-ptn-kicker">Change readiness</span>
      <div className="oc-ptn-ready-body">
        <LoanCreditGauge
          fraction={eligibilityChecked ? fraction : 0}
          tone={eligibilityChecked && allowed ? "ok" : eligibilityChecked ? "locked" : "active"}
          value={eligibilityChecked ? `${checksPassed}/${total || 0}` : "—"}
          label={eligibilityChecked ? (allowed ? "Ready" : "Blocked") : "Unchecked"}
          minLabel="0"
          maxLabel={String(total || 0)}
          foot={
            eligibilityChecked
              ? allowed
                ? "Eligible for partner changes."
                : failed[0]?.title || "Requirements failing."
              : "Run check in Eligibility tab."
          }
        />
        {eligibilityChecked && failed.length > 0 ? (
          <ul className="oc-ptn-blockers">
            {failed.slice(0, 2).map((check) => (
              <li key={check.id}>{check.title}</li>
            ))}
            {failed.length > 2 ? <li>+{failed.length - 2} more</li> : null}
          </ul>
        ) : null}
      </div>
    </section>
  );
};

const PartnerDeskBoard = ({
  members,
  inviteQuote,
  pendingRequest,
  pendingInvites,
  checks,
  eligibilityChecked,
  allowed,
  checksPassed,
}) => {
  const allocation = useMemo(() => computeShareAllocation(members), [members]);

  return (
    <div className="oc-ptn">
      <CapTableHero
        members={members}
        inviteQuote={inviteQuote}
        pendingRequest={pendingRequest}
        pendingInvites={pendingInvites}
      />
      <div className="oc-ptn-grid">
        <ShareDonut items={allocation.items} total={allocation.total} />
        <ReadinessCard
          eligibilityChecked={eligibilityChecked}
          allowed={allowed}
          checks={checks}
          checksPassed={checksPassed}
        />
      </div>
    </div>
  );
};

export default PartnerDeskBoard;
